// XP drops and level-up moments. Driven by watching the adventure log and skill_state change
// after a mutation, so they fire for auto-completed quests too — not just taps.
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keys, useAdventure, useSkillState, useSkills } from "../lib/api";
import { fmtXp } from "../lib/xp";
import type { AdventureEntry, SkillState } from "../lib/types";
import { Button } from "./ui";

interface Drop {
  id: number;
  skill: string;
  xp: number;
}

export function XpDrops() {
  const { data: state } = useSkillState();
  const { data: adventure } = useAdventure();
  const { data: skills } = useSkills();
  const qc = useQueryClient();
  const prevXp = useRef<Map<string, number> | null>(null);
  const seenLog = useRef<number | null>(null);
  const [drops, setDrops] = useState<Drop[]>([]);
  const [levelUp, setLevelUp] = useState<AdventureEntry | null>(null);
  const name = (id: string) => skills?.find((s) => s.id === id)?.name ?? id;

  useEffect(() => {
    if (!state) return;
    const now = new Map(state.map((s: SkillState) => [s.skill_id, s.xp]));
    if (prevXp.current) {
      const fresh: Drop[] = [];
      for (const [skill, xp] of now) {
        const before = prevXp.current.get(skill) ?? xp;
        if (xp > before) fresh.push({ id: Date.now() + Math.random(), skill, xp: xp - before });
      }
      if (fresh.length) {
        setDrops((d) => [...d, ...fresh]);
        window.setTimeout(() => setDrops((d) => d.filter((x) => !fresh.includes(x))), 1500);
      }
    }
    prevXp.current = now;
  }, [state]);

  useEffect(() => {
    if (!adventure?.length) return;
    const newest = adventure[0];
    if (seenLog.current === null) {
      seenLog.current = newest.id;
      return;
    }
    const unseen = adventure.filter((a) => a.id > (seenLog.current ?? 0));
    seenLog.current = newest.id;
    const big = unseen.find(
      (a) => a.kind === "level_up" || a.kind === "skill_unlocked" || a.kind === "streak",
    );
    if (big) setLevelUp(big);
  }, [adventure]);

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-[max(1rem,env(safe-area-inset-top))] z-40 flex flex-col items-center gap-1">
        {drops.map((d) => (
          <div
            key={d.id}
            className={`numeral animate-xp-drop rounded-chip bg-surface px-3 py-1 text-sm shadow-card ${d.skill === "recovery" ? "text-recovery" : "text-accent"}`}
          >
            +{fmtXp(d.xp)} {name(d.skill)}
          </div>
        ))}
      </div>
      {levelUp && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-6"
          onClick={() => setLevelUp(null)}
        >
          <div
            className="card card-glow animate-pop w-full max-w-sm p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs uppercase tracking-widest text-ink-mute">
              {levelUp.kind === "level_up"
                ? "Level up"
                : levelUp.kind === "skill_unlocked"
                  ? "New skill"
                  : "Streak"}
            </p>
            <h2 className="mt-2 text-3xl font-semibold">{levelUp.title}</h2>
            {levelUp.kind === "level_up" && typeof levelUp.detail.xp === "number" && (
              <p className="numeral mt-2 text-sm text-ink-soft">{fmtXp(levelUp.detail.xp)} xp</p>
            )}
            <Button
              className="mt-5 w-full"
              onClick={() => {
                setLevelUp(null);
                void qc.invalidateQueries({ queryKey: keys.skillState });
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
